import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { recomputeProjectTotal } from "@/hooks/use-budget-lines";
import { logProjectEvent } from "@/hooks/use-project-events";
import { isProjectLockedError, toastProjectLocked } from "@/lib/db-errors";
import { buildRevisionSummary, type BudgetLineSnapshot } from "@/lib/budget-revision";
import type { BudgetLineInsert, BudgetLineUpdate } from "@/hooks/use-budget-line-mutations";
import type { RevisionAssignmentAdjustment } from "@/lib/budget-revision-assignments";

/**
 * VIB-1202: Commit en budsjett-revisjon som én sekvens av budget_lines-
 * mutasjoner + ETT samlende `budget_revised`-event.
 *
 * Per-linje-audit skjer automatisk via DB-triggerne (budget_line_added/
 * updated/removed — sistnevnte utvidet med full to_jsonb-snapshot i VIB-1202).
 * `total_amount`/`bf_estimated` reberegnes av de eksisterende kanoniske
 * writerne (DB-trigger `compute_project_total` + `bf_estimated`-trigger; JS-
 * `recomputeProjectTotal` kalles som idempotent buffer). Ingen ny BF-variant.
 *
 * Vernet (reduksjon under loggede/fullførte timer, låst måned, sletting av
 * linje med loggede timer) håndheves i UI-laget FØR commit via de rene
 * funksjonene i `src/lib/budget-revision.ts` — denne hooken forutsetter en
 * ferdig validert draft og utfører skrivingen.
 *
 * Aktør: `budget_revised` logges med den FAKTISK innloggede brukeren
 * (`useAuth().user`), ikke impersonation-brukeren — det er et økonomi-event
 * (audit-regel 1).
 */

export interface RevisionUpdate {
  id: string;
  patch: BudgetLineUpdate;
  before: BudgetLineSnapshot;
  after: BudgetLineSnapshot;
}

export interface RevisionDelete {
  id: string;
  before: BudgetLineSnapshot;
}

export interface CommitRevisionParams {
  projectId: string;
  reason: string;
  updates: RevisionUpdate[];
  creates: BudgetLineInsert[];
  deletes: RevisionDelete[];
  assignmentAdjustments: RevisionAssignmentAdjustment[];
  totalBefore: number;
  totalAfter: number;
  bfEstimatedBefore: number;
  bfEstimatedAfter: number;
  /** useAuth().user.id — faktisk innlogget bruker (økonomi-aktør). */
  actorId: string | null;
}

export function useCommitBudgetRevision() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (params: CommitRevisionParams) => {
      if (!params.reason.trim()) {
        throw new Error("Revisjon krever en begrunnelse.");
      }

      // 1) Oppdater endrede linjer (DB-trigger logger budget_line_updated per rad).
      for (const u of params.updates) {
        const { error } = await supabase
          .from("budget_lines")
          .update(u.patch)
          .eq("id", u.id);
        if (error) throw new Error(`Oppdatering av linje feilet: ${error.message}`);
      }

      // 2) Keep assignments within the revised line hours. Every UPDATE/DELETE
      //    is audited by trg_log_assignment_change with an old/new snapshot.
      for (const adjustment of params.assignmentAdjustments) {
        if (adjustment.action === "delete") {
          const { error } = await supabase
            .from("budget_line_assignments")
            .delete()
            .eq("id", adjustment.assignmentId);
          if (error) throw new Error(`Fjerning av tildeling feilet: ${error.message}`);
        } else {
          const { error } = await supabase
            .from("budget_line_assignments")
            .update({ hours: adjustment.afterHours, hours_locked: true })
            .eq("id", adjustment.assignmentId);
          if (error) throw new Error(`Justering av tildeling feilet: ${error.message}`);
        }
      }

      // 3) Nye linjer (DB-trigger logger budget_line_added + capture baseline).
      if (params.creates.length > 0) {
        const { error } = await supabase.from("budget_lines").insert(params.creates);
        if (error) throw new Error(`Ny linje feilet: ${error.message}`);
      }

      // 4) Slett linjer (DB BEFORE DELETE-snapshot logger budget_line_removed
      //    med full to_jsonb(OLD)). Vernet mot sletting av linjer med loggede
      //    timer er allerede håndhevet i UI før commit.
      for (const d of params.deletes) {
        const { error } = await supabase.from("budget_lines").delete().eq("id", d.id);
        if (error) throw new Error(`Sletting av linje feilet: ${error.message}`);
      }

      // 5) Reberegn total (idempotent buffer — DB-trigger har allerede synket).
      await recomputeProjectTotal(params.projectId);

      // 6) Ett samlende revisjons-event med begrunnelse + sum-/tildelingseffekt.
      const summary = buildRevisionSummary({
        updates: params.updates.map((u) => ({ before: u.before, after: u.after })),
        creates: params.creates.map((c) => ({ description: c.description })),
        deletes: params.deletes.map((d) => ({ before: d.before })),
      });

      await logProjectEvent({
        projectId: params.projectId,
        userId: params.actorId,
        eventType: "budget_revised",
        entityType: "project",
        entityId: params.projectId,
        payload: {
          reason: params.reason.trim(),
          changes: summary.changes,
          updated_count: summary.updatedCount,
          created_count: summary.createdCount,
          deleted_count: summary.deletedCount,
          assignment_changes: params.assignmentAdjustments.map((adjustment) => ({
            assignment_id: adjustment.assignmentId,
            budget_line_id: adjustment.budgetLineId,
            line_description: adjustment.lineDescription,
            user_id: adjustment.userId,
            user_name: adjustment.userName,
            task_status: adjustment.taskStatus,
            before_hours: adjustment.beforeHours,
            after_hours: adjustment.afterHours,
            action: adjustment.action,
          })),
          total_before: params.totalBefore,
          total_after: params.totalAfter,
          bf_estimated_before: params.bfEstimatedBefore,
          bf_estimated_after: params.bfEstimatedAfter,
        },
      });
    },
    onSuccess: async (_data, vars) => {
      await invalidateFor(qc, "budget_lines", { projectId: vars.projectId });
      if (vars.assignmentAdjustments.length > 0) {
        await invalidateFor(qc, "budget_line_assignments", { projectId: vars.projectId });
      }
      await invalidateFor(qc, "project_events", { projectId: vars.projectId });
      toast.success("Budsjettet er revidert");
    },
    onError: (err, vars) => {
      if (isProjectLockedError(err)) {
        toastProjectLocked({ projectId: vars.projectId, navigate });
        return;
      }
      toast.error((err as Error).message);
    },
  });
}
